const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_SITE_KEY = process.env.TURNSTILE_SITE_KEY || '';
const TURNSTILE_TIMEOUT = 10000; // ms

let loadPromise = null;

const loadTurnstileScript = () => {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Turnstile is only available in browser environment'));
    }
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }
    if (loadPromise) return loadPromise;

    loadPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
        if (existingScript) {
            existingScript.addEventListener('load', () => resolve(window.turnstile));
            existingScript.addEventListener('error', err => {
                loadPromise = null;
                reject(err);
            });
            return;
        }

        const script = document.createElement('script');
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.turnstile);
        script.onerror = err => {
            loadPromise = null;
            reject(err);
        };
        document.head.appendChild(script);
    });
    return loadPromise;
};

const renderTurnstile = (action = 'sms_code', timeoutMs = TURNSTILE_TIMEOUT, containerElement = null) => new Promise((resolve, reject) => {
    let container;
    let isInternalContainer = false;

    if (containerElement) {
        container = containerElement;
    } else {
        container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.width = '0';
        container.style.height = '0';
        container.style.overflow = 'hidden';
        document.body.appendChild(container);
        isInternalContainer = true;
    }

    const cleanup = widgetId => {
        if (widgetId && window.turnstile && window.turnstile.remove) {
            window.turnstile.remove(widgetId);
        }
        if (isInternalContainer && container && container.parentNode) {
            container.parentNode.removeChild(container);
        }
    };

    try {
        let timeoutId = null;
        const widgetId = window.turnstile.render(container, {
            sitekey: TURNSTILE_SITE_KEY,
            // Turnstile 当前不接受 invisible，使用 flexible 并隐藏容器
            size: 'flexible',
            action,
            callback: token => {
                clearTimeout(timeoutId);
                // cleanup(widgetId); // 不要自动清除
                resolve(token);
            },
            'error-callback': () => {
                clearTimeout(timeoutId);
                // cleanup(widgetId); // 出错也不要自动清除，让用户重试
                reject(new Error('Turnstile error'));
            },
            'expired-callback': () => {
                clearTimeout(timeoutId);
                // cleanup(widgetId); // 过期也不要自动清除，让用户重试
                reject(new Error('Turnstile expired'));
            }
        });
        timeoutId = setTimeout(() => {
            cleanup(widgetId); // 超时还是清理一下比较好，或者保留也行，看需求
            reject(new Error('Turnstile timeout'));
        }, timeoutMs);
        // window.turnstile.execute(widgetId);
    } catch (err) {
        cleanup();
        reject(err);
    }
});

/**
 * 挂载 Turnstile 组件到指定容器，用于持续显示
 * @param {HTMLElement} container 容器元素
 * @param {Object} callbacks 回调函数 { onSuccess, onError, onExpired }
 * @returns {Promise<string>} widgetId
 */
export const mountTurnstile = async (container, callbacks = {}) => {
    const {onSuccess, onError, onExpired} = callbacks;
    if (!TURNSTILE_SITE_KEY) {
        console.error('TURNSTILE_SITE_KEY is not configured');
        if (onError) onError(new Error('TURNSTILE_SITE_KEY is not configured'));
        return;
    }
    
    try {
        const turnstile = await loadTurnstileScript();
        if (!turnstile) {
            throw new Error('Failed to load Turnstile');
        }

        // 清空容器内容，防止重复渲染
        container.innerHTML = '';

        const widgetId = turnstile.render(container, {
            sitekey: TURNSTILE_SITE_KEY,
            size: 'flexible', // 适应容器宽度
            callback: token => {
                if (onSuccess) onSuccess(token);
                // 不要移除 widget
            },
            'error-callback': () => {
                if (onError) onError(new Error('Turnstile error'));
            },
            'expired-callback': () => {
                if (onExpired) onExpired();
            }
        });
        return widgetId;
    } catch (err) {
        console.error('Mount Turnstile failed', err);
        if (onError) onError(err);
    }
};

export const removeTurnstile = (widgetId) => {
    if (window.turnstile && window.turnstile.remove && widgetId) {
        window.turnstile.remove(widgetId);
    }
};

export const getTurnstileToken = async (action = 'sms_code', timeoutMs = TURNSTILE_TIMEOUT, containerElement = null) => {
    if (!TURNSTILE_SITE_KEY) {
        throw new Error('TURNSTILE_SITE_KEY is not configured');
    }
    const turnstile = await loadTurnstileScript();
    if (!turnstile) {
        throw new Error('Failed to load Turnstile');
    }
    return renderTurnstile(action, timeoutMs, containerElement);
};


export const preloadTurnstile = () => {
    if (!TURNSTILE_SITE_KEY) return Promise.resolve(null);
    return loadTurnstileScript().catch(() => null);
};

export default getTurnstileToken;
